import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootStoreType } from 'src/types/store';
import { checkFormValidity } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import { CarrierType } from 'src/types/carrier';
import { addMessage } from 'src/resources/alertMessages/reducer';
import { setLocationParams } from 'src/resources/location/reducer';
import {
  clearCarrierDetail,
  createCarrierDetailFetch,
  deleteCarrierDetailFetch,
  getCarrierDetailFetch,
  updateCarrierDetailFetch,
} from 'src/resources/carrierDetail/reducer';
import TopBlock from './TopBlock';
import Detail from './Detail';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import deleteEmptyKeysAndValues from 'src/utils/deleteEmptyKeysAndValues';
import { FormErrorsType, OnChangeType } from 'src/types/global';
import { EditKeysType } from './constants';

const initData: CarrierType = { SHIPPER: undefined, COT: undefined } as any;

const CarrierDetailContainer: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { idCarrier } = useParams();
  const newCarrier = !idCarrier;

  const formRef = useRef<HTMLFormElement>(null);

  const data = useSelector((state: RootStoreType) => state.carrierDetail?.data);
  const loading = useSelector((state: RootStoreType) => state.carrierDetail?.loading);
  const deleted = useSelector((state: RootStoreType) => state.carrierDetail?.deleted);
  const shipperCodes = useSelector((state: RootStoreType) =>
    state.constantsCodes.data.shipperCodes
      ? [...state.constantsCodes.data.shipperCodes].sort((a, b) => a.label.localeCompare(b.label))
      : []
  );

  const [editData, setEditData] = useState<CarrierType>(initData);
  const [editErrors, setEditErrors] = useState<FormErrorsType>({});
  const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

  const permissionCreate = useCheckPermission('CARRIER.CREATE');
  const permissionUpdate = useCheckPermission('CARRIER.UPDATE');
  const disableEdit = newCarrier ? !permissionCreate : !permissionUpdate;

  const backUrl = '/admin/carriers/';

  useEffect(() => {
    if (idCarrier) {
      // Použijeme skutečné ID z načtených dat, pokud jsou k dispozici, jinak URL parametr
      const displayId = data?.ID !== undefined ? data.ID : idCarrier;
      dispatch(
        setLocationParams({ name: translate('carrierDetail.title', { idCarrier: displayId }), backUrl })
      );
    } else {
      dispatch(setLocationParams({ name: translate('carrierDetail.titleNew'), backUrl }));
    }
  }, [idCarrier, backUrl, data?.ID]);

  useEffect(() => {
    if (idCarrier) {
      dispatch(getCarrierDetailFetch(idCarrier));
    } else {
      setEditData(initData);
    }
    return () => {
      dispatch(clearCarrierDetail());
    };
  }, [idCarrier]);

  useEffect(() => {
    if (data) {
      setEditData({ ...data });
    }

    if (!idCarrier && data?.ID) {
      navigate('/admin/carriers/' + String(data.ID));
    }
  }, [data]);

  useEffect(() => {
    if (deleted) {
      navigate('/admin/carriers');
    }
  }, [deleted]);

  const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
    const newData = { ...editData };
    const newErrors = { ...editErrors };
    newData[key] = value;
    newErrors[key] = error;
    setEditData(newData);
    setEditErrors(newErrors);
  };

  const handleSave = () => {
    const newFormErrors = checkFormValidity(formRef);

    if (!Object.keys(newFormErrors).length) {
      const formatData = newCarrier ? deleteEmptyKeysAndValues(editData) : editData;

      if (newCarrier) {
        dispatch(createCarrierDetailFetch({ ...formatData }));
      } else if (idCarrier) {
        dispatch(updateCarrierDetailFetch({ id: idCarrier, data: formatData }));
      }
    } else {
      dispatch(
        addMessage({
          type: 'warning',
          title: translate('messages.formWarningTitle'),
          text: translate('messages.formWarningText'),
        })
      );
    }

    setEditErrors(newFormErrors);
  };

  const handleDelete = () => {
    setConfirmParams({
      title: translate('carrierDetail.deleteConfirm'),
      action: () => {
        if (idCarrier) dispatch(deleteCarrierDetailFetch(idCarrier));
      },
    });
  };

  return (
    <>
      <TopBlock newCarrier={newCarrier} disableEdit={disableEdit} handleSave={handleSave} handleDelete={handleDelete} />
      <form ref={formRef}>
        {loading && <Loading />}
        <Detail
          onChange={onChange}
          editData={editData}
          editErrors={editErrors}
          newCarrier={newCarrier}
          disableEdit={disableEdit}
          shipperCodes={shipperCodes}
        />
      </form>
      {confirmParams && (
        <ConfirmDialog
          title={confirmParams.title}
          content={confirmParams.content}
          customContent={confirmParams.customContent}
          options={confirmParams.options}
          optionsValue={confirmParams.optionsValue}
          action={confirmParams.action}
          onCancel={confirmParams.onCancel}
          onClose={() => setConfirmParams(null)}
        />
      )}
    </>
  );
};

export default CarrierDetailContainer;

